/**
 * MunicipalProfilePanel — the municipality's own published profile.
 *
 * Everything here is transcribed from sikhiotown.go.th's own pages, so it is
 * the one panel a Sikhio official can check line-by-line against their own
 * website. That makes provenance the whole point: the figures are 2013–2016
 * civil-register snapshots, the tier is "reference" not "live", and the
 * as-of date travels with each number rather than being buried in a tooltip.
 */

import type { FallbackTier, MunicipalityProfile } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  data: MunicipalityProfile | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const fmtInt = (n: number) => n.toLocaleString("en-US");

/** "2559 BE (September 2016) — …" <Arrow direction="right" /> "2559 BE" for a compact inline stamp. */
function shortAsOf(asOf: string | undefined): string | null {
  if (!asOf) return null;
  const m = /^(\d{4}\s*BE)/.exec(asOf.trim());
  return m ? m[1] : asOf.split("—")[0].trim().slice(0, 22);
}

export function MunicipalProfilePanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="col">
        <div className="eyebrow">เทศบาล / PROFILE</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "60%" }} />
      </div>
    );
  }
  if (!data) return null;

  const pop = data.population;
  const edu = data.education;
  const health = data.health;

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="เทศบาล "
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="sikhiotown.go.th"
      />

      {data.motto && (
        <div className="caption" style={{ color: "var(--text-2)", lineHeight: 1.5 }}>
          “{data.motto}”
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 12px", marginTop: 4 }}>
        <span className="caption">พื้นที่</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          {data.areaKm2} ตร.กม. · {fmtInt(data.areaRai)} ไร่
        </span>

        {pop && (
          <>
            <span className="caption">ประชากร</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              {fmtInt(pop.total)} คน
              {shortAsOf(pop.asOf) ? ` · ${shortAsOf(pop.asOf)}` : ""}
            </span>

            <span className="caption">ครัวเรือน</span>
            <span className="mono caption" style={{ textAlign: "right" }}>{fmtInt(pop.households)} หลัง</span>

            <span className="caption">ความหนาแน่น</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              {Math.round(pop.densityPerKm2).toLocaleString("en-US")} คน/ตร.กม.
            </span>
          </>
        )}

        <span className="caption">ชุมชน</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          {data.communityCount} ชุมชน · {data.tambons.length} ตำบล
        </span>

        {edu && (
          <>
            <span className="caption">การศึกษา</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              {edu.institutionCount} แห่ง · {fmtInt(edu.studentCount)} คน
            </span>
          </>
        )}

        {health && (
          <>
            <span className="caption">โรงพยาบาล</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              {health.hospitalBeds} เตียง · อสม. {fmtInt(health.villageHealthVolunteerCount)}
            </span>
          </>
        )}
      </div>

      {data.waterSources.length > 0 && (
        <>
          <div className="caption" style={{ marginTop: 6, color: "var(--text-3)" }}>
            แหล่งน้ำเพื่อการเกษตร
          </div>
          <div className="col" style={{ gap: 2 }}>
            {data.waterSources.map((w) => (
              <div key={w.name} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: "var(--size-eyebrow)" }}>{w.name}</span>
                <span className="mono caption" style={{ color: "var(--text-3)", whiteSpace: "nowrap" }}>
                  {w.insideMunicipality ? "ในเขต" : "นอกเขต"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="caption" style={{ marginTop: 4, color: "var(--text-3)" }}>
        ข้อมูลจากเว็บไซต์เทศบาลเอง · ปรับปรุงล่าสุดตามที่เทศบาลเผยแพร่ (2556–2559)
      </div>
    </div>
  );
}
